import { FC, useEffect, useMemo, useState } from 'react';

import { Typography, Flex, Select, Row, Spin } from 'antd';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useTheme } from 'styled-components';
import useSWR from 'swr';

import IconError from '@/assets/icons/icon-error.svg?react';
import IconInfo from '@/assets/icons/icon-info.svg?react';
import Card from '@/components/RCard';
import CustomGraphicTooltip from '@/components/RGraphicTooltip';
import RToggleButtons from '@/components/RToggleButtons';
import RTooltip from '@/components/RTooltip';
import useCurrency from '@/contexts/useCurrency';
import {
  SimulationResult,
  SimulationResultFinancialActivity,
} from '@/types/simulations';
import { getYears, insertLineBreak, formatValue } from '@/utils';

interface CustomLegendProp {
  payload: {
    color?: string;
    value: string;
    payload?: {
      value: number;
      percent: number;
    };
  }[];
  currencySymbol: string;
}

const CustomLegend: FC<CustomLegendProp> = ({ payload, currencySymbol }) => (
  <Flex vertical gap={16}>
    {payload?.map((entry, index) => (
      <Flex key={`item-${index}`} align="center" gap={8}>
        <div
          style={{
            width: '12px',
            height: '12px',
            backgroundColor: entry.color ?? 'transparent',
            borderRadius: '50%',
          }}
        ></div>
        <Flex vertical>
          <Typography.Text className="fs-14-medium text-gray-color">
            {insertLineBreak(entry.value ?? '', 4)}
          </Typography.Text>

          <div>
            <Typography.Text className="fs-14-bold text-gray-color">
              {currencySymbol}
              {formatValue(entry.payload?.value ?? 0)}
            </Typography.Text>
            <Typography.Text className="fs-14-medium text-gray-color">
              {' '}
              {`(${((entry.payload?.percent ?? 0) * 100).toFixed(1)}%)`}
            </Typography.Text>
          </div>
        </Flex>
      </Flex>
    ))}
  </Flex>
);

const Earnings = () => {
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();
  const resultId = useParams<Record<string, string>>();
  const [storageSelection, setStorageSelection] = useState('withStorage');
  const [selectedYear, setSelectedYear] = useState<number | undefined>();
  const [selectedToggleIndex, setSelectedToggleIndex] = useState<number>(1); // 0: Year, 1: All Years
  const { data: simulationData } = useSWR<SimulationResult>(
    `/simulation/${resultId.resultId}/`,
    {
      revalidateOnFocus: false,
    }
  );
  const theme = useTheme();

  const handleStorageSelection = (value: string) => {
    setStorageSelection(value);
  };

  const years = useMemo(() => {
    return simulationData
      ? getYears(simulationData.startDate, simulationData.endDate)
      : [];
  }, [simulationData]);

  useEffect(() => {
    if (selectedYear !== undefined) {
      setSelectedToggleIndex(0); // Year seçildiğinde toggle'ı Year'a çevir
    }
  }, [selectedYear]);

  const getEndpoint = () => {
    if (selectedToggleIndex === 1) {
      // All Years seçili
      return `/simulation/${resultId.resultId}/financial_activity/`;
    } else if (selectedToggleIndex === 0 && selectedYear) {
      // Year seçili ve yıl seçilmiş
      const startDate = `${selectedYear}-01-01T00:00:00Z`;
      const endDate = `${selectedYear}-12-31T23:59:59Z`;
      return `/simulation/${resultId.resultId}/financial_activity/?endDatetime=${endDate}&startDatetime=${startDate}&timeUnit=year`;
    }
    return null;
  };
  const endpoint = getEndpoint();
  const {
    data: financialActivityData,
    isLoading,
    error,
  } = useSWR<SimulationResultFinancialActivity>(endpoint, {
    revalidateOnFocus: false,
  });

  const getPieData = () => {
    if (!financialActivityData) return [];

    if (storageSelection === 'withoutStorage') {
      return [
        {
          name: t('result.finance.earnings.revenueTypes.directlySoldFromPlant'),
          value: financialActivityData.graphData.reduce(
            (acc, item) => acc + (item.revenueWos ?? 0),
            0
          ),
          color: theme.colors.greenSecondary,
          unit: currencySymbol,
        },
      ];
    }

    if (storageSelection === 'storage') {
      return [
        // {
        //   name: t('result.finance.earnings.revenueTypes.directlySoldFromPlant'),
        //   value: financialActivityData.graphData.reduce(
        //     (acc, item) => acc + item.revenueDirectlySoldStorage,
        //     0
        //   ),
        //   color: theme.colors.greenSecondary,
        //   unit: currencySymbol,
        // },
        {
          name: t('result.finance.earnings.revenueTypes.dischargedFromPlant'),
          value: financialActivityData.graphData.reduce(
            (acc, item) => acc + item.revenueFromChargedEnergyFromPlant,
            0
          ),
          color: theme.colors.orangeSecondary,
          unit: currencySymbol,
        },
        {
          name: t('result.finance.earnings.revenueTypes.arbitrage'),
          value: financialActivityData.graphData.reduce(
            (acc, item) => acc + item.revenueFromArbitrage,
            0
          ),
          color: theme.colors.purpleSecondary,
          unit: currencySymbol,
        },
        {
          name: t('result.finance.earnings.revenueTypes.ancillaryServices'),
          value: financialActivityData.graphData.reduce(
            (acc, item) => acc + item.revenueFromAncillaryServices,
            0
          ),
          color: theme.colors.blueLight,
          unit: currencySymbol,
        },
      ];
    }

    // Default case (withStorage)
    return [
      {
        name: t('result.finance.earnings.revenueTypes.directlySoldFromPlant'),
        value: financialActivityData.graphData.reduce(
          (acc, item) => acc + item.revenueFromPlant,
          0
        ),
        color: theme.colors.greenSecondary,
        unit: currencySymbol,
      },
      {
        name: t('result.finance.earnings.revenueTypes.dischargedFromPlant'),
        value: financialActivityData.graphData.reduce(
          (acc, item) => acc + item.revenueFromChargedEnergyFromPlant,
          0
        ),
        color: theme.colors.orangeSecondary,
        unit: currencySymbol,
      },
      {
        name: t('result.finance.earnings.revenueTypes.arbitrage'),
        value: financialActivityData.graphData.reduce(
          (acc, item) => acc + item.revenueFromArbitrage,
          0
        ),
        color: theme.colors.purpleSecondary,
        unit: currencySymbol,
      },
      {
        name: t('result.finance.earnings.revenueTypes.ancillaryServices'),
        value: financialActivityData.graphData.reduce(
          (acc, item) => acc + item.revenueFromAncillaryServices,
          0
        ),
        color: theme.colors.blueLight,
        unit: currencySymbol,
      },
    ];
  };

  const getCostValue = () => {
    if (!financialActivityData?.graphData.length) return 0;

    if (storageSelection === 'withoutStorage') {
      return financialActivityData.graphData[0].cogsWos ?? 0;
    }
    if (storageSelection === 'storage') {
      return (
        financialActivityData.graphData[0].costOfPurchasedEnergyStorage ?? 0
      );
    }
    return financialActivityData.graphData[0].costOfPurchasedEnergy ?? 0;
  };

  const data = getPieData();

  const isDataZero =
    data.length === 0 || data.every((item) => item.value === 0);

  const handleSwitchButtonChange = (index: number) => {
    setSelectedToggleIndex(index);
    if (index === 0) {
      // Year butonu
      if (years.length > 0) {
        setSelectedYear(years[0]);
      }
    } else if (index === 1) {
      // All Years butonu
      setSelectedYear(undefined);
    }
  };

  const handleYearChange = (value: number) => {
    setSelectedYear(value);
  };

  // Toggle butonlar için index tabanlı yaklaşım
  const toggleLabels = [
    t('result.finance.earnings.toggleButtons.year', {
      number: selectedYear ? years.indexOf(selectedYear) + 1 : 1,
    }),
    t('result.finance.earnings.toggleButtons.allYears'),
  ];

  return (
    <Row className="w-100">
      <Card
        title={
          <Flex gap={8} align="center">
            <Typography.Title level={4} className="fs-17-bold">
              {t('result.finance.earnings.title')}
            </Typography.Title>
            <RTooltip
              title={t('result.finance.earnings.title')}
              description={t('result.finance.earnings.tooltip')}
            >
              <IconInfo />
            </RTooltip>
          </Flex>
        }
        extra={
          <Flex gap={16} align="center">
            <motion.div
              whileTap={{ scale: 1.1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 24 }}
            >
              <Select
                placeholder={t(
                  'result.finance.earnings.storageSelection.placeholder'
                )}
                style={{ minWidth: '150px', width: '150px' }}
                value={storageSelection}
                onChange={handleStorageSelection}
              >
                <Select.Option value="withStorage">
                  {t('result.finance.earnings.storageSelection.withStorage')}
                </Select.Option>
                <Select.Option value="withoutStorage">
                  {t('result.finance.earnings.storageSelection.withoutStorage')}
                </Select.Option>
                <Select.Option value="storage">
                  {t('result.finance.earnings.storageSelection.storage')}
                </Select.Option>
              </Select>
            </motion.div>
            <RToggleButtons
              labels={toggleLabels}
              selectedIndex={selectedToggleIndex}
              onIndexChange={handleSwitchButtonChange}
            />
            <motion.div
              whileTap={{ scale: 1.1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 24 }}
            >
              <Select
                placeholder={t('result.finance.earnings.years.placeholder')}
                style={{ minWidth: '89px', width: '89px' }}
                value={selectedYear?.toString()}
                onChange={(value) => handleYearChange(Number(value))}
                disabled={selectedToggleIndex === 1}
              >
                {years.map((year) => (
                  <Select.Option key={year} value={year}>
                    {year}
                  </Select.Option>
                ))}
              </Select>
            </motion.div>
          </Flex>
        }
        $paddingBody="32px 24px 0px"
        styles={{ header: { padding: '14px 24px' } }}
        style={{
          width: '100%',
          borderEndStartRadius: '0px',
          borderEndEndRadius: '0px',
          marginTop: '24px',
        }}
      >
        {isLoading ? (
          <Flex justify="center" align="center" style={{ height: '332px' }}>
            <Spin style={{ margin: '20px' }} />
          </Flex>
        ) : error || isDataZero ? (
          <Flex
            vertical
            gap={12}
            justify="center"
            align="center"
            style={{ height: 328 }}
          >
            <IconError width={50} height={50} fill={theme.colors.tagFailure} />
            {error && (
              <Typography.Text className="fs-14-regular text-gray-color text-center">
                {t('result.error.fetchDataError')}
              </Typography.Text>
            )}
            {isDataZero && (
              <Typography.Text className="fs-14-regular text-gray-color text-center">
                {t('result.finance.cashFlow.noDataAvailable')}
              </Typography.Text>
            )}
          </Flex>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={255}>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={125}
                  innerRadius={85}
                  fill={theme.colors.purpleSecondary}
                  dataKey="value"
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomGraphicTooltip />} />
                <Legend
                  content={(props) => (
                    <CustomLegend
                      payload={
                        (props.payload ?? []) as CustomLegendProp['payload']
                      }
                      currencySymbol={currencySymbol}
                    />
                  )}
                  verticalAlign="middle"
                  align="right"
                  layout="vertical"
                  iconSize={12}
                />
              </PieChart>
            </ResponsiveContainer>

            <Flex
              vertical
              style={{
                padding: '13.5px 0px',
                width: '100%',
                marginTop: '32px',
              }}
            >
              <Typography.Text className="fs-12-bold text-gray-color">
                {t('result.finance.earnings.costOfPurchasedEnergy')}
                {currencySymbol}
                {formatValue(getCostValue())}
              </Typography.Text>
            </Flex>
          </>
        )}
      </Card>
      <Flex
        style={{
          padding: '22.5px 24px',
          border: `0.5px solid ${theme.colors.grayLightAccent}`,
          width: '100%',
          height: '83px',
        }}
      >
        <Typography.Text className="fs-12-regular text-gray-color">
          {t('result.finance.earnings.revenueNote')}
        </Typography.Text>
      </Flex>
    </Row>
  );
};
export default Earnings;
